import React, { useState,useEffect } from "react";
import {
  InfoOutlined,
  PersonOutlined,
  BusinessOutlined,
  PhoneOutlined,
  EmailOutlined,
  LocationOnOutlined,
  StarOutlined,
  FlagOutlined,
} from "@mui/icons-material";
import { Box, Chip, Grid } from "@mui/material";
import InformationTile from "components/Shared/InformationTile";
import InfoGridSkeleton from "components/Shared/Skeleton/InfoGridSkeleton";
import { useVendorLeadDetailContext } from "contexts/VendorLeadContext";
import { localizeDateTimeShort } from "utils/dateFormats";
import { reverseGeocode } from "utils/reverseGeocode";

const STATUS_COLOR = {
  New: "default",
  Qualified: "primary",
  Negotiation: "warning",
  QuoteSent: "info",
  Won: "success",
  Lost: "error",
  Onboarding: "secondary",
  Active: "success",
};

const VendorLeadBasicDetail = () => {
  const { vendorLeadDetail, loadingVendorLeadDetail } =
    useVendorLeadDetailContext();
  const [warehouseAddress, setWarehouseAddress] = useState("Loading...");
  const [corporateAddress, setCorporateAddress] = useState("Loading...");

  useEffect(() => {
    if (!vendorLeadDetail) return;

    reverseGeocode(
      vendorLeadDetail.warehouseAddress?.lat,
      vendorLeadDetail.warehouseAddress?.lon
    ).then(setWarehouseAddress);

    reverseGeocode(
      vendorLeadDetail.corporateAddress?.lat,
      vendorLeadDetail.corporateAddress?.lon
    ).then(setCorporateAddress);

  }, [vendorLeadDetail]);

  if (loadingVendorLeadDetail) return <InfoGridSkeleton />;


  return (
    <Box padding={3}>
      <Grid container spacing={4}>

        {/* Lead ID */}
        <Grid item xs={12} sm={6} md={3}>
          <InformationTile
            icon={() => <InfoOutlined color="primary" />}
            title="Lead ID"
            subtitle={vendorLeadDetail?.leadId || "N/A"}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <InformationTile
            icon={() => <InfoOutlined color="primary" />}
            title="Source Type"
            subtitle={vendorLeadDetail?.vendorLeadSourceType?.name || "N/A"}
          />
        </Grid>

        {/* Status */}
        <Grid item xs={12} sm={6} md={3}>
          <InformationTile
            icon={() => <FlagOutlined color="primary" />}
            title="Status"
            subtitle={
              vendorLeadDetail?.status ? (
                <Chip
                  size="small"
                  label={vendorLeadDetail.status}
                  color={STATUS_COLOR[vendorLeadDetail.status] ?? "default"}
                />
              ) : (
                "N/A"
              )
            }
          />
        </Grid>

        {/* Full Name */}
        <Grid item xs={12} sm={6} md={3}>
          <InformationTile
            icon={() => <PersonOutlined color="primary" />}
            title="Name"
            subtitle={
              vendorLeadDetail?.name ||
              "N/A"
            }
          />
        </Grid>

        {/* Company */}
        <Grid item xs={12} sm={6} md={3}>
          <InformationTile
            icon={() => <BusinessOutlined color="primary" />}
            title="Company"
            subtitle={vendorLeadDetail?.companyName || "N/A"}
          />
        </Grid>

        {/* Contact Person */}
        <Grid item xs={12} sm={6} md={3}>
          <InformationTile
            icon={() => <PersonOutlined color="primary" />}
            title="Contact Person Name"
            subtitle={
              vendorLeadDetail?.contactPersonName ||
              "N/A"
            }
          />
        </Grid>

        {/* Phone */}
        <Grid item xs={12} sm={6} md={3}>
          <InformationTile
            icon={() => <PhoneOutlined color="primary" />}
            title="Phone"
            subtitle={vendorLeadDetail?.phone || "N/A"}
          />
        </Grid>

        {/* Alternate Phone */}
        <Grid item xs={12} sm={6} md={3}>
          <InformationTile
            icon={() => <PhoneOutlined color="primary" />}
            title="Alternate Phone"
            subtitle={vendorLeadDetail?.alternatePhone || "N/A"}
          />
        </Grid>

        {/* Email */}
        <Grid item xs={12} sm={6} md={3}>
          <InformationTile
            icon={() => <EmailOutlined color="primary" />}
            title="Email"
            subtitle={vendorLeadDetail?.email || "N/A"}
          />
        </Grid>

        {/* Country */}
        <Grid item xs={12} sm={6} md={3}>
          <InformationTile
            icon={() => <FlagOutlined color="primary" />}
            title="Country"
            subtitle={vendorLeadDetail?.country?.name || "N/A"}
          />
        </Grid>

        {/* Warehouse Address */}
        <Grid item xs={12} sm={6} md={3}>
          <InformationTile
            icon={() => <LocationOnOutlined color="primary" />}
            title="Warehouse Address"
            subtitle={warehouseAddress}
          />
        </Grid>

        {/* Corporate Address */}
        <Grid item xs={12} sm={6} md={3}>
          <InformationTile
            icon={() => <LocationOnOutlined color="primary" />}
            title="Corporate Address"
            subtitle={corporateAddress}
          />
        </Grid>

        {/* Responsible */}
        <Grid item xs={12} sm={6} md={3}>
          <InformationTile
            icon={() => <PersonOutlined color="primary" />}
            title="Responsible"
            subtitle={vendorLeadDetail?.responsible?.name || "Unassigned"}
          />
        </Grid>

        {/* Score */}
        <Grid item xs={12} sm={6} md={3}>
          <InformationTile
            icon={() => <StarOutlined color="primary" />}
            title="Score"
            subtitle={
              vendorLeadDetail?.score != null
                ? `${vendorLeadDetail.score} / 100`
                : "N/A"
            }
          />
        </Grid>

        {/* Stage Group */}
        <Grid item xs={12} sm={6} md={3}>
          <InformationTile
            icon={() => <InfoOutlined color="primary" />}
            title="Stage Group"
            subtitle={vendorLeadDetail?.stageGroup || "N/A"}
          />
        </Grid>

        {/* Created At */}
        <Grid item xs={12} sm={6} md={3}>
          <InformationTile
            icon={() => <InfoOutlined color="primary" />}
            title="Created At"
            subtitle={
              localizeDateTimeShort(vendorLeadDetail?.createdAt) || "N/A"
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <InformationTile
            icon={() => <InfoOutlined color="primary" />}
            title="Created By"
            subtitle={
              vendorLeadDetail?.createdBy?.name || "N/A"
            }
          />
        </Grid>

        {/* Updated At */}
        <Grid item xs={12} sm={6} md={3}>
          <InformationTile
            icon={() => <InfoOutlined color="primary" />}
            title="Updated At"
            subtitle={
              localizeDateTimeShort(vendorLeadDetail?.updatedAt) || "N/A"
            }
          />
        </Grid>

      </Grid>
    </Box>
  );
};

export default VendorLeadBasicDetail;